import { type ReactElement, type ReactNode, useEffect, useRef } from "react";
import { DisplayFieldShell } from "./DisplayFieldShell";
import {
	type DisplayFieldBaseProps,
	DisplayFieldContent,
	DisplayFieldFeedback,
	DisplayFieldState,
	resolveDisplayContent,
} from "./display-field.shared";

export interface DisplayTextareaProps extends DisplayFieldBaseProps {
	/** Multi-line displayed value when populated. */
	value?: ReactNode;
}

/**
 * Multi-line underline field for longer text in drawers / detail panels.
 * When `state` is Active, the value row becomes an inline textarea.
 *
 * @see Figma DisplayTextarea 3355:26402
 */
export function DisplayTextarea({
	label,
	value,
	state = DisplayFieldState.Enabled,
	content: contentProp,
	leadingIcon,
	showLeadingIcon,
	hintText,
	showHintText,
	feedback = DisplayFieldFeedback.Idle,
	showEdit,
	onEdit,
	editAriaLabel,
	editValue,
	onEditChange,
	onEditCommit,
	onEditCancel,
	className,
	"data-testid": dataTestId = "display-textarea",
}: DisplayTextareaProps): ReactElement {
	const content = resolveDisplayContent(contentProp, value);
	const isPopulated = content === DisplayFieldContent.Populated;
	const isDisabled = state === DisplayFieldState.Disabled;
	const isActive = state === DisplayFieldState.Active;
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const draft =
		editValue ??
		(typeof value === "string" || typeof value === "number"
			? String(value)
			: "");

	useEffect(() => {
		if (!isActive) return;
		const el = textareaRef.current;
		if (!el) return;
		el.focus();
		const len = el.value.length;
		el.setSelectionRange(len, len);
	}, [isActive]);

	return (
		<DisplayFieldShell
			label={label}
			state={state}
			isPopulated={isPopulated || isActive}
			isMultiline
			leadingIcon={leadingIcon}
			showLeadingIcon={showLeadingIcon}
			hintText={hintText}
			showHintText={showHintText}
			feedback={feedback}
			showEdit={(showEdit ?? true) && !isDisabled}
			onEdit={onEdit}
			editAriaLabel={editAriaLabel}
			onClear={
				isActive
					? () => {
							onEditChange?.("");
						}
					: undefined
			}
			clearAriaLabel={`Clear ${label}`}
			className={className}
			data-testid={dataTestId}
		>
			{isActive ? (
				<textarea
					ref={textareaRef}
					aria-label={label}
					value={draft}
					rows={3}
					onChange={(event) => onEditChange?.(event.target.value)}
					onBlur={() => onEditCommit?.()}
					onKeyDown={(event) => {
						if (event.key === "Escape") {
							event.preventDefault();
							event.stopPropagation();
							onEditCancel?.();
						}
					}}
					className="w-full min-w-0 cursor-text resize-none border-0 bg-transparent p-0 font-display text-sm font-extrabold text-primary outline-none placeholder:font-semibold placeholder:text-quaternary"
					placeholder={label}
				/>
			) : null}
			{!isActive && isPopulated ? (
				<p className="w-full font-display text-sm font-extrabold whitespace-pre-wrap text-primary">
					{value}
				</p>
			) : null}
		</DisplayFieldShell>
	);
}
